/**
 * InfiniteImageCarousel - Smooth infinite scrolling image gallery
 * - Auto-scroll with smooth animation
 * - Loop infinitely (duplicates images for seamless loop)
 * - Touch/drag support for mobile
 * - Pause on hover
 * - Responsive design
 */
import { useState, useEffect, useRef, useCallback } from "react";

export default function InfiniteImageCarousel({ 
  images = [], 
  speed = 30,          // pixels per second
  pauseOnHover = true,
  className = "",
  imageClassName = "",
  gap = 16             // gap between images in pixels
}) {
  const containerRef = useRef(null);
  const [isPaused, setIsPaused] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const animationRef = useRef(null);
  const lastTimeRef = useRef(null);

  // Duplicate images for seamless infinite loop
  const duplicatedImages = images.length > 0 
    ? [...images, ...images, ...images] 
    : [];

  // Animation loop
  const animate = useCallback((currentTime) => {
    if (!containerRef.current || isPaused || isDragging) {
      lastTimeRef.current = currentTime;
      animationRef.current = requestAnimationFrame(animate);
      return;
    }

    if (lastTimeRef.current !== null) {
      const deltaTime = (currentTime - lastTimeRef.current) / 1000; // seconds
      const distance = speed * deltaTime;
      
      containerRef.current.scrollLeft += distance;

      // Reset scroll position for seamless loop
      const scrollWidth = containerRef.current.scrollWidth;
      const clientWidth = containerRef.current.clientWidth;
      const maxScroll = scrollWidth - clientWidth;
      const oneThird = scrollWidth / 3;

      // When we've scrolled past the first set of images, jump back
      if (containerRef.current.scrollLeft >= oneThird * 2) {
        containerRef.current.scrollLeft -= oneThird;
      }
    }

    lastTimeRef.current = currentTime;
    animationRef.current = requestAnimationFrame(animate);
  }, [speed, isPaused, isDragging]);

  // Start/stop animation
  useEffect(() => {
    if (images.length > 0) {
      animationRef.current = requestAnimationFrame(animate);
    }
    
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [animate, images.length]);

  // Initialize scroll position to middle
  useEffect(() => {
    if (containerRef.current && images.length > 0) {
      const oneThird = containerRef.current.scrollWidth / 3;
      containerRef.current.scrollLeft = oneThird;
    }
  }, [images]);

  // Mouse/Touch handlers for manual scrolling
  const handleMouseDown = (e) => {
    setIsDragging(true);
    setStartX(e.pageX - containerRef.current.offsetLeft);
    setScrollLeft(containerRef.current.scrollLeft);
    containerRef.current.style.cursor = "grabbing";
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    if (containerRef.current) {
      containerRef.current.style.cursor = "grab";
    }
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const x = e.pageX - containerRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    containerRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleTouchStart = (e) => {
    setIsDragging(true);
    setStartX(e.touches[0].pageX - containerRef.current.offsetLeft);
    setScrollLeft(containerRef.current.scrollLeft);
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    const x = e.touches[0].pageX - containerRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    containerRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  if (images.length === 0) {
    return null;
  }

  return (
    <div 
      className={`relative overflow-hidden ${className}`}
      onMouseEnter={() => pauseOnHover && setIsPaused(true)}
      onMouseLeave={() => {
        setIsPaused(false);
        setIsDragging(false);
        if (containerRef.current) {
          containerRef.current.style.cursor = "grab";
        }
      }}
    >
      {/* Gradient overlays for smooth edges */}
      <div className="absolute left-0 top-0 bottom-0 w-12 sm:w-20 bg-gradient-to-r from-[#0A0A0A] to-transparent z-10 pointer-events-none" />
      <div className="absolute right-0 top-0 bottom-0 w-12 sm:w-20 bg-gradient-to-l from-[#0A0A0A] to-transparent z-10 pointer-events-none" />
      
      {/* Scrolling container */}
      <div
        ref={containerRef}
        className="flex overflow-x-hidden cursor-grab select-none"
        style={{ gap: `${gap}px` }}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onMouseMove={handleMouseMove}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {duplicatedImages.map((img, index) => (
          <div
            key={`${img}-${index}`}
            className={`flex-shrink-0 ${imageClassName}`}
          >
            <div className="relative aspect-[4/3] w-[200px] sm:w-[280px] md:w-[320px] lg:w-[360px] rounded-xl overflow-hidden group">
              <img
                src={img}
                alt={`Prosjekt ${(index % images.length) + 1}`}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                draggable="false"
              />
              {/* Hover overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="absolute bottom-3 left-3 right-3">
                  <span className="text-white text-sm font-medium">
                    Oslo prosjekt #{(index % images.length) + 1}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Scroll hint - mobile only */}
      <div className="sm:hidden absolute bottom-2 left-1/2 transform -translate-x-1/2 flex items-center gap-1 text-[#64748B] text-xs">
        <span>Swipe</span>
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
        </svg>
      </div>
    </div>
  );
}
